import { useState, useEffect, useRef } from 'react';
import { Dialog, Drawer, Portal, Box, Button, Input, Text, VStack, HStack, useBreakpointValue } from '@chakra-ui/react';
import { MdAdd as AddIcon, MdCheck as CheckIcon, MdClose as CloseIcon } from 'react-icons/md';

export interface Collection {
  id: string;
  title: string;
  recipeIds?: string[];
}

export interface CollectionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collections: Collection[];
  isRecipeInCollection: (collection: Collection) => boolean;
  addToCollection: (collectionId: string) => void;
  pending: boolean;
  onCreateAndAdd: (name: string) => void;
  createPending: boolean;
}

export default function CollectionsDialog({
  open,
  onOpenChange,
  collections,
  isRecipeInCollection,
  addToCollection,
  pending,
  onCreateAndAdd,
  createPending,
}: CollectionsDialogProps) {
  // Match the established BottomNavBar pattern — no { ssr: false }
  const isMobile = useBreakpointValue({ base: true, lg: false });

  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setIsCreateFormOpen(false);
      setNewCollectionName('');
    }
  }, [open]);

  // Auto-focus the input on desktop only — mobile keyboard obscures content
  const isDesktop = useBreakpointValue({ base: false, lg: true }, { ssr: false });
  useEffect(() => {
    if (!isDesktop || !isCreateFormOpen) return;
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateFormOpen]);

  const handleCancel = () => {
    setIsCreateFormOpen(false);
    setNewCollectionName('');
  };

  const handleSubmit = () => {
    const trimmed = newCollectionName.trim();
    if (!trimmed || createPending) return;
    onCreateAndAdd(trimmed);
  };

  const isSubmitDisabled = !newCollectionName.trim() || createPending;

  const dragHandle = (
    <Box pt={3} pb={1} display="flex" justifyContent="center">
      <Box w="36px" h="4px" borderRadius="full" bg="border.muted" />
    </Box>
  );

  const closeButton = (
    <Box
      as="button"
      aria-label="Close"
      onClick={() => onOpenChange(false)}
      w="30px"
      h="30px"
      borderRadius="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="text.muted"
      bg="bg.muted"
      border="none"
      cursor="pointer"
      outline="none"
      _hover={{ bg: 'border.muted', color: 'text.primary' }}
      _focusVisible={{ boxShadow: 'none', outline: 'none' }}
      transition="all 0.15s"
      flexShrink={0}
    >
      <CloseIcon size={15} />
    </Box>
  );

  const headerContent = (
    <Box display="flex" alignItems="center" justifyContent="space-between" w="100%">
      <Text fontFamily="heading" fontWeight="800" fontSize="lg" color="text.primary">Add to Collection</Text>
      {closeButton}
    </Box>
  );

  const bodyContent = (
    <VStack gap={0} align="stretch">
      {collections.length > 0 && (
        <VStack gap={2} align="stretch" maxH="260px" overflowY="auto" mb={4}>
          {collections.map((collection: Collection) => {
            const already = isRecipeInCollection(collection);
            return (
              <Button
                key={collection.id}
                onClick={() => !already && addToCollection(collection.id)}
                loading={pending}
                variant="outline"
                justifyContent="flex-start"
                h="44px"
                disabled={pending || already || createPending}
                borderColor="border.muted"
                color={already ? 'text.muted' : 'text.primary'}
                bg="white"
                opacity={already ? 0.6 : 1}
                _hover={{ bg: already ? 'white' : 'bg.canvas', borderColor: 'border.muted' }}
              >
                <HStack gap={2}>
                  <Box color={already ? 'text.muted' : 'accent.emphasis'} fontSize="md">
                    {already ? <CheckIcon /> : <AddIcon />}
                  </Box>
                  <Text fontSize="sm" fontWeight={already ? '400' : '500'}>{collection.title}{already && ' (Added)'}</Text>
                </HStack>
              </Button>
            );
          })}
        </VStack>
      )}

      {!isCreateFormOpen ? (
        <Button
          variant="outline"
          justifyContent="center"
          color="accent.secondary"
          borderColor="accent.secondaryBorder"
          bg="accent.secondarySubtle"
          fontWeight="500"
          fontSize="sm"
          h="44px"
          onClick={() => setIsCreateFormOpen(true)}
          disabled={pending || createPending}
          _hover={{ bg: 'accent.secondarySubtle', borderColor: 'accent.secondary' }}
          style={{ borderStyle: 'dashed' }}
        >
          <AddIcon size={16} />
          New collection
        </Button>
      ) : (
        <VStack gap={3} align="stretch">
          <Input
            ref={inputRef}
            placeholder="Collection name"
            value={newCollectionName}
            onChange={(e) => setNewCollectionName(e.target.value)}
            disabled={createPending}
            bg="white"
            borderColor="border.muted"
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          />
          <HStack gap={2}>
            <Button
              flex={1}
              bg="accent.secondary"
              color="white"
              _hover={{ bg: 'accent.secondaryHover' }}
              fontWeight="600"
              fontSize="sm"
              onClick={handleSubmit}
              loading={createPending}
              disabled={isSubmitDisabled}
            >
              Create &amp; add
            </Button>
            <Button
              variant="ghost"
              color="text.muted"
              fontWeight="400"
              fontSize="sm"
              onClick={handleCancel}
              disabled={createPending}
            >
              Cancel
            </Button>
          </HStack>
        </VStack>
      )}
    </VStack>
  );

  if (isMobile) {
    return (
      <Drawer.Root open={open} onOpenChange={(e) => onOpenChange(e.open)} placement="bottom">
        <Portal>
          <Drawer.Backdrop style={{ background: 'rgba(0,0,0,0.6)' }} />
          <Drawer.Positioner>
            <Drawer.Content
              bg="bg.surface"
              roundedTop="2xl"
              pb="calc(env(safe-area-inset-bottom, 0px) + 16px)"
            >
              {dragHandle}
              <Drawer.Header borderBottomWidth="1px" borderColor="border.muted" bg="bg.surface" pt={3} pb={4} px={5}>
                {headerContent}
              </Drawer.Header>
              <Drawer.Body pb={6} pt={4} px={5}>
                {bodyContent}
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => onOpenChange(e.open)}>
      <Dialog.Backdrop style={{ background: 'rgba(0,0,0,0.6)' }} />
      <Dialog.Positioner>
        <Dialog.Content bg="bg.surface" border="1px solid" borderColor="border.muted" shadow="lg" maxW="sm" borderRadius="2xl" overflow="hidden">
          {dragHandle}
          <Dialog.Header borderBottomWidth="1px" borderColor="border.muted" bg="bg.surface" pt={3} pb={4} px={5}>
            {headerContent}
          </Dialog.Header>
          <Dialog.Body pb={6} pt={4} px={5}>
            {bodyContent}
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
